import type { Theme, ThemeColors, ThemeMood } from '../types/theme';
import { getAccessibleTextColor, isDarkColor } from './color';
import { generateDesignMd } from './designMdExporter';

type SourceKind = 'design_md' | 'screenshot' | 'url' | 'custom';
type Density = ThemeMood['density'];

const DEFAULT_THEME: Theme = {
  id: 'custom-lab',
  name: 'Custom Lab',
  description: 'A custom DesignLab theme extracted from user-provided style material.',
  sourceDesignMd: 'custom',
  visualKeywords: ['custom', 'design system', 'preview'],
  colors: {
    background: '#f8fafc',
    surface: '#eef2f7',
    surfaceElevated: '#fbfdff',
    textPrimary: '#111827',
    textSecondary: '#475569',
    textMuted: '#64748b',
    border: '#cbd5e1',
    borderSubtle: '#e2e8f0',
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    accent: '#2563eb',
    onPrimary: '#f8fafc',
    success: '#16a34a',
    warning: '#d97706',
    error: '#dc2626',
  },
  typography: {
    headingFont: 'Inter, system-ui, sans-serif',
    bodyFont: 'Inter, system-ui, sans-serif',
    monoFont: 'JetBrains Mono, monospace',
    headingWeight: '700',
    bodyWeight: '400',
    letterSpacingHeading: '0em',
  },
  radius: {
    button: '10px',
    card: '12px',
    panel: '16px',
    badge: '9999px',
    input: '10px',
  },
  shadow: {
    card: '0 1px 2px rgba(15, 23, 42, 0.08)',
    elevated: '0 16px 40px rgba(15, 23, 42, 0.14)',
  },
  layout: {
    maxWidth: '1200px',
    sectionSpacing: '80px',
    cardPadding: '24px',
    navHeight: '64px',
  },
  mood: {
    density: 'medium',
    tone: 'restrained, practical, product-led',
    productFit: ['AI product', 'SaaS tool', 'Design workflow'],
    bestFor: ['Style previews', 'Product dashboards', 'AI app landing pages'],
    notGoodFor: ['Highly decorative campaigns', 'Brand-heavy launches'],
  },
};

interface ThemeOverrides {
  id?: string;
  name?: string;
  description?: string;
  sourceDesignMd?: string;
  sourceKind?: SourceKind;
  sourceLabel?: string;
}

export interface CustomThemeDraft {
  designMd: string;
  sourceKind: SourceKind;
  sourceLabel: string;
  theme: Theme;
}

export interface ThemeAdjustments {
  density?: Density;
  primary?: string;
  radiusScale?: number;
}

export function getDefaultCustomTheme(): Theme {
  return cloneTheme(DEFAULT_THEME);
}

export function normalizeTheme(input: unknown, fallback = DEFAULT_THEME): Theme {
  if (!input || typeof input !== 'object') {
    return cloneTheme(fallback);
  }

  const value = input as Partial<Theme>;
  const fallbackTheme = cloneTheme(fallback);

  return {
    ...fallbackTheme,
    ...value,
    id: typeof value.id === 'string' && value.id.trim() ? value.id : fallbackTheme.id,
    name: typeof value.name === 'string' && value.name.trim() ? value.name : fallbackTheme.name,
    description: typeof value.description === 'string' && value.description.trim() ? value.description : fallbackTheme.description,
    sourceDesignMd: typeof value.sourceDesignMd === 'string' && value.sourceDesignMd.trim() ? value.sourceDesignMd : fallbackTheme.sourceDesignMd,
    visualKeywords: Array.isArray(value.visualKeywords) && value.visualKeywords.length > 0
      ? value.visualKeywords.filter((item): item is string => typeof item === 'string')
      : fallbackTheme.visualKeywords,
    colors: { ...fallbackTheme.colors, ...(value.colors || {}) },
    typography: { ...fallbackTheme.typography, ...(value.typography || {}) },
    radius: { ...fallbackTheme.radius, ...(value.radius || {}) },
    shadow: { ...fallbackTheme.shadow, ...(value.shadow || {}) },
    layout: { ...fallbackTheme.layout, ...(value.layout || {}) },
    mood: {
      ...fallbackTheme.mood,
      ...(value.mood || {}),
      productFit: Array.isArray(value.mood?.productFit) ? value.mood.productFit : fallbackTheme.mood.productFit,
      bestFor: Array.isArray(value.mood?.bestFor) ? value.mood.bestFor : fallbackTheme.mood.bestFor,
      notGoodFor: Array.isArray(value.mood?.notGoodFor) ? value.mood.notGoodFor : fallbackTheme.mood.notGoodFor,
    },
  };
}

export function createThemeFromDesignMd(markdown: string, overrides: ThemeOverrides = {}): CustomThemeDraft {
  const title = extractTitle(markdown) || overrides.name || 'Imported DESIGN.md';
  const colors = extractColorRoles(markdown);
  const fonts = extractFonts(markdown);
  const radii = extractRadii(markdown);
  const density = extractDensity(markdown);
  const tone = extractTone(markdown);
  const primary = colors.primary || colors.accent || DEFAULT_THEME.colors.primary;
  const background = colors.background || DEFAULT_THEME.colors.background;
  const surface = colors.surface || deriveSurface(background);
  const textPrimary = colors.textPrimary || getAccessibleTextColor(DEFAULT_THEME.colors.textPrimary, background);
  const onPrimary = colors.onPrimary || getAccessibleTextColor(DEFAULT_THEME.colors.onPrimary, primary);

  const theme = normalizeTheme({
    ...DEFAULT_THEME,
    id: overrides.id || `imported-${slugify(title)}`,
    name: overrides.name || title,
    description: overrides.description || 'Imported from a DESIGN.md style document.',
    sourceDesignMd: overrides.sourceDesignMd || 'Imported DESIGN.md',
    visualKeywords: extractKeywords(markdown),
    colors: {
      ...DEFAULT_THEME.colors,
      background,
      surface,
      surfaceElevated: colors.surfaceElevated || (isDarkColor(background) ? '#111827' : '#ffffff'),
      textPrimary,
      textSecondary: colors.textSecondary || getAccessibleTextColor(DEFAULT_THEME.colors.textSecondary, background, 3),
      textMuted: colors.textMuted || getAccessibleTextColor(DEFAULT_THEME.colors.textMuted, background, 3),
      border: colors.border || deriveBorder(background),
      borderSubtle: colors.borderSubtle || colors.border || deriveBorder(background),
      primary,
      primaryHover: colors.primaryHover || darkenHex(primary, 0.12),
      accent: colors.accent || primary,
      onPrimary,
      success: colors.success || DEFAULT_THEME.colors.success,
      warning: colors.warning || DEFAULT_THEME.colors.warning,
      error: colors.error || DEFAULT_THEME.colors.error,
    },
    typography: {
      ...DEFAULT_THEME.typography,
      headingFont: fonts.headingFont || DEFAULT_THEME.typography.headingFont,
      bodyFont: fonts.bodyFont || DEFAULT_THEME.typography.bodyFont,
      monoFont: fonts.monoFont || DEFAULT_THEME.typography.monoFont,
      headingWeight: fonts.headingWeight || DEFAULT_THEME.typography.headingWeight,
      bodyWeight: fonts.bodyWeight || DEFAULT_THEME.typography.bodyWeight,
    },
    radius: {
      ...DEFAULT_THEME.radius,
      button: radii.button || DEFAULT_THEME.radius.button,
      card: radii.card || DEFAULT_THEME.radius.card,
      panel: radii.panel || DEFAULT_THEME.radius.panel,
      badge: radii.badge || DEFAULT_THEME.radius.badge,
      input: radii.input || radii.button || DEFAULT_THEME.radius.input,
    },
    mood: {
      ...DEFAULT_THEME.mood,
      density,
      tone,
    },
  });

  return {
    designMd: markdown,
    sourceKind: overrides.sourceKind || 'design_md',
    sourceLabel: overrides.sourceLabel || title,
    theme,
  };
}

export function createThemeFromPalette(
  palette: string[],
  overrides: ThemeOverrides = {},
): CustomThemeDraft {
  const uniquePalette = dedupeHexColors(palette);
  const primary = uniquePalette[0] || DEFAULT_THEME.colors.primary;
  const background = pickBackground(uniquePalette);
  const surface = deriveSurface(background);
  const textPrimary = getAccessibleTextColor(DEFAULT_THEME.colors.textPrimary, background);
  const onPrimary = getAccessibleTextColor(DEFAULT_THEME.colors.onPrimary, primary);
  const title = overrides.name || 'Extracted Palette';

  const theme = normalizeTheme({
    ...DEFAULT_THEME,
    id: overrides.id || `extracted-${Date.now()}`,
    name: title,
    description: overrides.description || 'Extracted from visual source material.',
    sourceDesignMd: overrides.sourceDesignMd || 'Extracted source',
    visualKeywords: ['extracted', 'palette', 'custom'],
    colors: {
      ...DEFAULT_THEME.colors,
      background,
      surface,
      surfaceElevated: isDarkColor(background) ? '#111827' : '#ffffff',
      textPrimary,
      textSecondary: getAccessibleTextColor(DEFAULT_THEME.colors.textSecondary, background, 3),
      textMuted: getAccessibleTextColor(DEFAULT_THEME.colors.textMuted, background, 3),
      border: deriveBorder(background),
      primary,
      primaryHover: darkenHex(primary, 0.12),
      accent: uniquePalette[1] || primary,
      onPrimary,
    },
  });

  return {
    designMd: generateDesignMd(theme, 'landing'),
    sourceKind: overrides.sourceKind || 'screenshot',
    sourceLabel: overrides.sourceLabel || title,
    theme,
  };
}

export function applyThemeAdjustments(theme: Theme, adjustments: ThemeAdjustments): Theme {
  const nextTheme = cloneTheme(theme);

  if (adjustments.primary && isHexColor(adjustments.primary)) {
    nextTheme.colors.primary = adjustments.primary;
    nextTheme.colors.primaryHover = darkenHex(adjustments.primary, 0.12);
    nextTheme.colors.accent = adjustments.primary;
    nextTheme.colors.onPrimary = getAccessibleTextColor(nextTheme.colors.onPrimary, adjustments.primary);
  }

  if (adjustments.density) {
    nextTheme.mood.density = adjustments.density;
    nextTheme.layout.cardPadding = adjustments.density === 'high' ? '18px' : adjustments.density === 'low' ? '32px' : '24px';
    nextTheme.layout.sectionSpacing = adjustments.density === 'high' ? '56px' : adjustments.density === 'low' ? '104px' : '80px';
  }

  if (typeof adjustments.radiusScale === 'number') {
    nextTheme.radius.button = `${Math.round(8 * adjustments.radiusScale)}px`;
    nextTheme.radius.input = nextTheme.radius.button;
    nextTheme.radius.card = `${Math.round(12 * adjustments.radiusScale)}px`;
    nextTheme.radius.panel = `${Math.round(16 * adjustments.radiusScale)}px`;
  }

  return nextTheme;
}

export function createAdvancedExportBundle(theme: Theme, pageType: 'landing' | 'dashboard', prompt: string): string {
  return [
    `# ${theme.name} Handoff Pack`,
    '',
    '## DESIGN.md',
    '',
    generateDesignMd(theme, pageType),
    '',
    '## Prompt',
    '',
    prompt,
    '',
    '## Token JSON',
    '',
    '```json',
    JSON.stringify(theme, null, 2),
    '```',
  ].join('\n');
}

export function extractHexColors(text: string): string[] {
  return dedupeHexColors(text.match(/#[0-9a-f]{3,6}\b/gi) || []);
}

function extractColorRoles(markdown: string): Partial<ThemeColors> {
  const colors: Partial<ThemeColors> = {};
  const lines = markdown.split('\n');
  const roleMatchers: Array<[keyof ThemeColors, RegExp]> = [
    ['primary', /primary(?!\s*text)|brand|主色|主操作/i],
    ['primaryHover', /primary\s*hover|hover|悬停/i],
    ['accent', /accent|link|强调|链接/i],
    ['background', /background|page|背景/i],
    ['surfaceElevated', /surface\s*elevated|elevated|抬升/i],
    ['surface', /surface|card|panel|表面|卡片|面板/i],
    ['textPrimary', /text\s*primary|foreground|主文字|正文/i],
    ['textSecondary', /text\s*secondary|secondary\s*text|次级文字/i],
    ['textMuted', /muted|caption|弱文字|辅助文字/i],
    ['borderSubtle', /border\s*subtle|subtle\s*border|浅边框/i],
    ['border', /border|divider|边框|分割/i],
    ['onPrimary', /on\s*primary|button\s*text|主操作文字/i],
    ['success', /success|成功/i],
    ['warning', /warning|警告/i],
    ['error', /error|danger|错误|危险/i],
  ];

  for (const line of lines) {
    const color = line.match(/#[0-9a-f]{3,6}\b/i)?.[0];
    if (!color) {
      continue;
    }

    const match = roleMatchers.find(([, regex]) => regex.test(line));
    if (match && !colors[match[0]]) {
      colors[match[0]] = normalizeHex(color);
    }
  }

  const fallbackColors = extractHexColors(markdown);
  colors.primary ||= fallbackColors[0];
  colors.background ||= fallbackColors.find((color) => !isDarkColor(color) && getColorLightness(color) > 0.88);
  colors.surface ||= fallbackColors.find((color) => color !== colors.background && getColorLightness(color) > 0.78);
  colors.textPrimary ||= fallbackColors.find((color) => getColorLightness(color) < 0.24);

  return colors;
}

function extractFonts(markdown: string) {
  const findValue = (patterns: RegExp[]) => {
    for (const pattern of patterns) {
      const match = markdown.match(pattern);
      if (match?.[1]) {
        return match[1].trim().replace(/^["'`]|["'`]$/g, '');
      }
    }
    return undefined;
  };

  return {
    headingFont: findValue([/heading\s*font\s*[:|]\s*([^|\n]+)/i, /标题字体\s*[:：|]\s*([^|\n]+)/i]),
    bodyFont: findValue([/body\s*font\s*[:|]\s*([^|\n]+)/i, /正文字体\s*[:：|]\s*([^|\n]+)/i]),
    monoFont: findValue([/mono\s*font\s*[:|]\s*([^|\n]+)/i, /等宽字体\s*[:：|]\s*([^|\n]+)/i]),
    headingWeight: findValue([/heading\s*weight\s*[:|]\s*([^|\n]+)/i, /标题字重\s*[:：|]\s*([^|\n]+)/i]),
    bodyWeight: findValue([/body\s*weight\s*[:|]\s*([^|\n]+)/i, /正文字重\s*[:：|]\s*([^|\n]+)/i]),
  };
}

function extractRadii(markdown: string) {
  const findRadius = (patterns: RegExp[]) => {
    for (const pattern of patterns) {
      const match = markdown.match(pattern);
      if (match?.[1]) {
        return normalizeCssSize(match[1]);
      }
    }
    return undefined;
  };

  return {
    button: findRadius([/button[^#\n|]*?(\d+(?:\.\d+)?(?:px|rem))/i, /按钮圆角[^#\n|]*?(\d+(?:\.\d+)?(?:px|rem))/i]),
    card: findRadius([/card[^#\n|]*?(\d+(?:\.\d+)?(?:px|rem))/i, /卡片圆角[^#\n|]*?(\d+(?:\.\d+)?(?:px|rem))/i]),
    panel: findRadius([/panel[^#\n|]*?(\d+(?:\.\d+)?(?:px|rem))/i, /面板圆角[^#\n|]*?(\d+(?:\.\d+)?(?:px|rem))/i]),
    badge: findRadius([/badge[^#\n|]*?(\d+(?:\.\d+)?(?:px|rem)|9999px)/i, /徽章圆角[^#\n|]*?(\d+(?:\.\d+)?(?:px|rem)|9999px)/i]),
    input: findRadius([/input[^#\n|]*?(\d+(?:\.\d+)?(?:px|rem))/i, /输入框圆角[^#\n|]*?(\d+(?:\.\d+)?(?:px|rem))/i]),
  };
}

function extractDensity(markdown: string): Density {
  if (/high density|dense|compact|高密度|紧凑/i.test(markdown)) {
    return 'high';
  }
  if (/low density|spacious|airy|低密度|宽松/i.test(markdown)) {
    return 'low';
  }
  return 'medium';
}

function extractTone(markdown: string): string {
  const match = markdown.match(/(?:tone|atmosphere|design philosophy|整体语气|视觉氛围|设计哲学)\s*[:：|]\s*([^|\n]+)/i);
  return match?.[1]?.trim() || DEFAULT_THEME.mood.tone;
}

function extractKeywords(markdown: string): string[] {
  const match = markdown.match(/(?:visual keywords|keywords|视觉关键词)\s*[:：]\s*([^\n]+)/i);
  if (!match?.[1]) {
    return DEFAULT_THEME.visualKeywords;
  }

  const keywords = match[1]
    .split(/[,，、]/)
    .map((item) => item.trim())
    .filter(Boolean);

  return keywords.length > 0 ? keywords.slice(0, 8) : DEFAULT_THEME.visualKeywords;
}

function extractTitle(markdown: string): string | undefined {
  const frontmatterName = markdown.match(/^name:\s*(.+)$/im)?.[1]?.trim();
  if (frontmatterName) {
    return frontmatterName;
  }

  return markdown.match(/^#\s+(.+)$/m)?.[1]?.replace(/design system/i, '').trim();
}

function pickBackground(colors: string[]): string {
  const light = colors.find((color) => getColorLightness(color) > 0.88);
  if (light) {
    return light;
  }

  const dark = colors.find((color) => getColorLightness(color) < 0.18);
  return dark || DEFAULT_THEME.colors.background;
}

function deriveSurface(background: string): string {
  return isDarkColor(background) ? lightenHex(background, 0.08) : darkenHex(background, 0.04);
}

function deriveBorder(background: string): string {
  return isDarkColor(background) ? lightenHex(background, 0.18) : darkenHex(background, 0.14);
}

function normalizeCssSize(size: string): string {
  return size.trim().toLowerCase();
}

function dedupeHexColors(colors: string[]): string[] {
  return [...new Set(colors.map(normalizeHex).filter(isHexColor))];
}

function isHexColor(color: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(normalizeHex(color));
}

function normalizeHex(color: string): string {
  const raw = color.trim().toLowerCase();
  const match = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) {
    return raw;
  }

  if (match[1].length === 6) {
    return `#${match[1]}`;
  }

  return `#${match[1].split('').map((char) => char + char).join('')}`;
}

function getColorLightness(color: string): number {
  const hex = normalizeHex(color).replace('#', '');
  if (hex.length !== 6) {
    return 0.5;
  }
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  return Math.max(r, g, b);
}

function darkenHex(color: string, amount: number): string {
  return shiftHex(color, -amount);
}

function lightenHex(color: string, amount: number): string {
  return shiftHex(color, amount);
}

function shiftHex(color: string, amount: number): string {
  const hex = normalizeHex(color).replace('#', '');
  if (hex.length !== 6) {
    return color;
  }

  const values = [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));
  const shifted = values.map((value) => {
    const nextValue = amount > 0
      ? value + (255 - value) * amount
      : value * (1 + amount);
    return Math.max(0, Math.min(255, Math.round(nextValue))).toString(16).padStart(2, '0');
  });

  return `#${shifted.join('')}`;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || 'theme';
}

function cloneTheme(theme: Theme): Theme {
  return JSON.parse(JSON.stringify(theme)) as Theme;
}
